import Decimal from 'decimal.js';

import {
  getIntegratedAssetsFromValuation,
  getManuallyTrackedAssetsFromValuation,
} from '@/modules/assets/FullScreenStructuredAssetsModal/FullScreenStructuredAssetsModal.utils';
import { BUSINESS_ENTITY_KINDS } from '@/modules/entities/entities.constants';
import { extractPoliciesFromEntity } from '@/modules/estate/EstateDirectHoldings/DirectHoldingsGroupContainer/DirectHoldingsGroupCard/DirectHoldingsGroupSection/HoldingsGroupPolicies/HoldingsGroupPolicies.utils';
import { EstatePage_EntityFragment } from '@/modules/estate/graphql/EstatePageData.generated';
import { LiabilitiesTable_LiabilityFragment } from '@/modules/liabilities/LiabilitiesTable/graphql/LiabilitiesTable.generated';

export function sumHoldingsGroupCurrentValue(
  holdingsGroup: EstatePage_EntityFragment[],
  clientProfileIds?: string[]
): Decimal {
  return holdingsGroup.reduce(
    (sum, holding) => {
      const subtype = holding.subtype;
      const isBusinessEntity = BUSINESS_ENTITY_KINDS.includes(holding.kind);
      let value = new Decimal(0);
      if (isBusinessEntity && clientProfileIds) {
        // For business entities with client profiles, calculate ownership-weighted value
        const currentValue = new Decimal(subtype.currentValue ?? 0);

        // Find ownership stakes for the specified client profiles
        const relevantStakes = (holding.ownedByStakes ?? []).filter(
          (stake) =>
            stake?.owningIndividual?.id &&
            clientProfileIds.includes(stake.owningIndividual.id)
        );

        // Sum the ownership percentages for relevant clients
        const totalOwnershipPercentage = relevantStakes.reduce(
          (total, stake) => total.add(stake?.ownershipPercentage ?? 0),
          new Decimal(0)
        );

        value = currentValue.times(
          totalOwnershipPercentage.div(new Decimal(100))
        );
      } else {
        value = new Decimal(subtype.currentValue ?? 0);
      }

      return sum.add(value);
    },

    new Decimal(0)
  );
}

export function sumGrantorControlledValue(
  businesses: EstatePage_EntityFragment[]
): Decimal {
  return businesses.reduce((sum, business) => {
    switch (business.subtype.__typename) {
      case 'SoleProprietorshipBusinessEntity':
      case 'CCorpBusinessEntity':
      case 'LLCBusinessEntity':
      case 'LPBusinessEntity':
      case 'SCorpBusinessEntity':
      case 'GPBusinessEntity':
        return sum.add(
          new Decimal(business.subtype.grantorControlledValue ?? 0)
        );
      default:
        return sum.add(new Decimal(business.subtype.currentValue ?? 0));
    }
  }, new Decimal(0));
}

export function sumLiabilities(
  liabilities: LiabilitiesTable_LiabilityFragment[]
): Decimal {
  return liabilities.reduce((sum, liability) => {
    if (!!liability.currentAmount) {
      return sum.add(liability.currentAmount);
    }
    return sum;
  }, new Decimal(0));
}

/**
 * Gets the ownership percentage for a specific client profile from a business holdings group
 */
export function getClientOwnershipPercentage(
  holdingsGroup: EstatePage_EntityFragment,
  clientProfileId: string
): Decimal | null {
  if (
    !BUSINESS_ENTITY_KINDS.includes(holdingsGroup.kind) ||
    !holdingsGroup.ownedByStakes
  ) {
    return null;
  }

  const clientStake = holdingsGroup.ownedByStakes.find(
    (stake) => stake?.owningIndividual?.id === clientProfileId
  );

  return clientStake?.ownershipPercentage ?? null;
}

export function holdingsGroupHasManuallyTrackedAssets(
  holdingsGroup: EstatePage_EntityFragment
): boolean {
  const designerAccount = holdingsGroup?.subtype.designerAccount ?? null;
  const valuation = designerAccount?.valuations.edges?.[0]?.node ?? null;
  return getManuallyTrackedAssetsFromValuation(valuation).length > 0;
}

export function holdingsGroupHasIntegratedAssets(
  holdingsGroup: EstatePage_EntityFragment
): boolean {
  const designerAccount = holdingsGroup?.subtype.designerAccount ?? null;
  const valuation = designerAccount?.valuations.edges?.[0]?.node ?? null;
  return getIntegratedAssetsFromValuation(valuation).length > 0;
}

export function holdingsGroupHasAssets(
  holdingsGroup: EstatePage_EntityFragment
): boolean {
  return (
    holdingsGroupHasManuallyTrackedAssets(holdingsGroup) ||
    holdingsGroupHasIntegratedAssets(holdingsGroup)
  );
}

export function holdingsGroupHasPolicies(
  holdingsGroup: EstatePage_EntityFragment
): boolean {
  const policies = extractPoliciesFromEntity(holdingsGroup);
  return policies.length > 0;
}

/**
 * Checks if a holdings group has any content (assets, liabilities, or policies)
 */
export function holdingsGroupHasContent(
  holdingsGroup: EstatePage_EntityFragment,
  liabilities: LiabilitiesTable_LiabilityFragment[]
): boolean {
  const hasLiabilities = liabilities.length > 0;

  const hasIntegratedAssets = holdingsGroupHasIntegratedAssets(holdingsGroup);
  const hasManuallyTrackedAssets =
    holdingsGroupHasManuallyTrackedAssets(holdingsGroup);
  const hasAssets = hasManuallyTrackedAssets || hasIntegratedAssets;

  const hasPolicies = holdingsGroupHasPolicies(holdingsGroup);

  return hasAssets || hasLiabilities || hasPolicies;
}
